import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserModulesPageQuery } from '@entities/Module';
import { useAuthStore } from '@entities/User';
import {
  ActiveSessionHero,
  EmptyModulesState,
  isDashboardTourArmed,
  ReminderBanner,
  StreakStrip,
  StudyHero,
  TodayCard,
  TodayPlanCard,
  useActiveSessionQuery,
  useDashboardStatsQuery,
} from '@features/Dashboard';
import { useDailyTaskQuery } from '@features/DailyTask';
import { EmailVerifyBanner } from '@features/EmailVerify';
import { useClaimDemoWords } from '@features/Stories';
import { trackEvent } from '@shared/analytics';
import { buildUnitPath, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, Chip } from '@shared/ui';
import { useIsSidebarMobile } from '@widgets/Sidebar';
import { CreateWordCard } from './CreateWordCard';
import { DailiesCard } from './DailiesCard';
import { DashboardTour } from './DashboardTour';
import { GameBanner } from './GameBanner';
import { GrammarCard } from './GrammarCard';
import { useWideBoard } from './useWideBoard';
import './DashboardPage.css';

/** Тот же запрос модулей, что у строки создания на «Словах и модулях» (общий кэш): главной нужно только, есть ли
 *  модули. */
const HOME_MODULES = { page: 1, size: 100, status: 'available' } as const;

/**
 * Главная — «что сделать сегодня» (решение владельца 2026-10-23, перестройка навигации): приветствие; «Тренировка на
 * сегодня» (план дня или продолжение, внизу — серия и когда она даст жетоны), баннер игры «Выживи в Лондоне», плашка
 * «На сегодня» («Задание дня» и «Миссия дня» — где получить жетоны) и «Встретилось новое слово?»; плашки «подтверди
 * почту» и «включи напоминания». Модули,
 * прогресс, «Недавно добавленные» и строка создания — на «Словах и модулях», «+ Карточка» ведёт туда. Колонка одна,
 * не шире 1200 px («дай воздуха» — владелец, 2026-10-22). Каждая плашка — высотой по своему содержимому, а не по соседу
 * (владелец 2026-10-26, «очень много воздуха»): от 880 px колонки — две независимые колонки (вариант A: слева
 * тренировка, игра, «новое слово»; справа «На сегодня», грамматика), уже — стопкой; раскладка — useWideBoard и
 * контейнерные запросы (DashboardPage.css).
 */
export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsSidebarMobile();
  const user = useAuthStore((s) => s.user);
  // Слова из итогов демо — в модуль «Выживи в Лондоне» и сразу к нему (2026-10-25); без подтверждения почты (2026-10-30).
  useClaimDemoWords(!!user, (moduleId) => navigate(buildUnitPath(moduleId)), true);
  const modulesQuery = useUserModulesPageQuery(HOME_MODULES);
  const activeQuery = useActiveSessionQuery();
  const activeSession = activeQuery.data;
  const dailyQuery = useDailyTaskQuery();
  const firstName = user?.name?.split(' ')[0]?.trim() || t('training.friend');
  const noModules = modulesQuery.data?.total === 0;
  const hasModules = !!modulesQuery.data && !noModules;
  // Серия и план дня (решение владельца 2026-10-21): сводка по истории тренировок и отдельная кнопка «Тренироваться» —
  // она ведёт в список модулей, готовую тренировку не запускает.
  const stats = useDashboardStatsQuery();
  const plan = stats.data?.plan ?? null;
  // «Задание дня» скелетон ждёт только в первый раз. Не загрузилось — плашка «На сегодня» при монтировании запрашивает
  // его снова, запрос без данных опять становится pending, и главная без конца переключалась между скелетоном и
  // содержимым, повторяя запросы (найдено 2026-10-26 при проверке перепланировки).
  const [dailySettled, setDailySettled] = useState(false);
  if (!dailySettled && !dailyQuery.isPending) setDailySettled(true);
  const loading =
    modulesQuery.isPending || activeQuery.isPending || stats.isPending || (dailyQuery.isPending && !dailySettled);
  // Знакомство с разделами после онбординга (решение владельца 2026-10-23: три шага по навигации) — когда главная
  // загрузилась.
  const [tourOn, setTourOn] = useState(isDashboardTourArmed);
  const tourReady = tourOn && !loading;
  // Две колонки или стопка — по ширине колонки главной, до первой отрисовки (ТЗ перепланировки верха, § 5).
  const boardRef = useRef<HTMLDivElement>(null);
  const wide = useWideBoard(boardRef);
  const gap = isMobile ? 'gap-3' : 'gap-5';

  const openTraining = () => {
    trackEvent('today_train_clicked', { repeat: plan?.repeat ?? 0, done_today: plan?.doneToday ?? 0 });
    navigate(paths.training);
  };

  return (
    // Поля и вертикаль колонки — из isMobile, а не через nav:: nav: — это min-width 900px, а useIsSidebarMobile считает
    // телефоном и ровно 900 px.
    <div className={cn('w-full animate-fadeIn', isMobile ? 'px-4 sm:px-6' : 'px-12')}>
      <div className={cn('dash mx-auto max-w-[1200px] pb-24', isMobile ? 'pt-16' : 'pt-10')}>
        <header className={cn('dash-head', isMobile ? 'mb-4' : 'mb-8')}>
          <div className={cn(!isMobile && 'flex flex-wrap items-center gap-x-4 gap-y-2.5')}>
            {/* В приветствии — имя: amp-mask / ym-hide-content прячут его в записях сессий. */}
            <h1
              className={cn(
                'dash-title amp-mask ym-hide-content text-balance font-serif font-medium tracking-[-0.02em]',
                isMobile ? 'text-[30px] leading-[1.1] sm:text-[36px]' : 'text-[44px] leading-[53px]',
              )}
            >
              {t('dashboard.greeting', { name: firstName })}
            </h1>
            {user?.hasDonated && (
              <div className={cn(isMobile && 'mt-3')}>
                <Chip variant="accent">♥ {t('dashboard.donorBadge')}</Chip>
              </div>
            )}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted">{t('dashboard.greetingSub')}</p>
        </header>
        {/* Просьба о почте — плашкой вверху (решение владельца 2026-10-21). */}
        {user && !user.emailVerified && (
          <EmailVerifyBanner
            variant="dock"
            stacked={isMobile}
            email={user.email}
            className={isMobile ? 'mb-3' : 'mb-5'}
            onResend={() => trackEvent('home_composer_locked_action', { reason: 'email', action: 'resend' })}
          />
        )}
        {/* Напоминания выключены — одна плашка там же, где просьба о почте; без почты и без слов письмам не о чем. */}
        {user?.emailVerified && hasModules && (
          <div className={isMobile ? 'mb-3' : 'mb-5'}>
            <ReminderBanner />
          </div>
        )}
        {/* Контейнер раскладки (DashboardPage.css): от 880 px — две независимые колонки (useWideBoard), уже — стопкой. */}
        <div ref={boardRef} className={cn('dash-board flex flex-col', wide && 'dash-board--wide', gap)}>
          {loading ? (
            // Скелетон той же геометрии, что после загрузки: высоты — по вёрстке и ширине колонки (DashboardPage.css),
            // справа — «На сегодня» с двумя строками.
            <div className={cn('dash-top', gap)} aria-hidden>
              <div className={cn('dash-col', gap)}>
                <div className={cn('dash-skel-today animate-skeleton rounded-card bg-card', isMobile && 'dash-skel--phone')} />
                <div className={cn('dash-skel-game animate-skeleton rounded-card bg-card', isMobile && 'dash-skel--phone')} />
              </div>
              {wide && (
                <div className={cn('dash-col', gap)}>
                  <div className="dash-skel-dailies animate-skeleton rounded-card bg-card" />
                </div>
              )}
            </div>
          ) : modulesQuery.isError ? (
            <div role="alert" className="rounded-card border border-solid border-line bg-card p-6">
              <p className="mb-4 text-muted">{t('training.loadError')}</p>
              <Button onClick={() => void modulesQuery.refetch()}>{t('training.retry')}</Button>
            </div>
          ) : (
            // Каждый блок — один раз; порядок в DOM = порядок чтения: от 880 px — левая колонка сверху вниз, потом
            // правая; уже — тренировка, игра, «На сегодня», грамматика, «новое слово» (ТЗ перепланировки, § 5).
            <div className={cn('dash-top', gap)}>
              {/* Слева — учиться: тренировка (модулей нет — «Здесь пока пусто»), сразу под ней игра (владелец
                  2026-10-22), на широкой колонке — «Встретилось новое слово?» (вариант A, владелец 2026-10-26). */}
              <div className={cn('dash-col dash-col-main', gap)}>
                {noModules ? (
                  // Новый модуль — в модалке, потом «Слова и модули» со строкой создания и этим модулем
                  // (ModuleCreatorModal). Игре модули не нужны — новичку она тоже на виду.
                  <EmptyModulesState emailInDock />
                ) : (
                  <div className="dash-today-slot min-w-0">
                    <TodayCard
                      phone={isMobile}
                      strip={
                        stats.data && (
                          <StreakStrip
                            streak={stats.data.streak}
                            weekDays={stats.data.weekDays}
                            dayCounted={stats.data.dayCounted}
                            reward={stats.data.streakReward}
                            phone={isMobile}
                          />
                        )
                      }
                    >
                      {activeSession ? (
                        <ActiveSessionHero
                          session={activeSession}
                          phone={isMobile}
                          onContinue={() =>
                            navigate(paths.session + '?moduleId=' + encodeURIComponent(activeSession.moduleId), {
                              state: { fromDashboard: true },
                            })
                          }
                          onChooseOther={() => navigate(paths.training)}
                        />
                      ) : plan ? (
                        <TodayPlanCard
                          plan={plan}
                          // Спокойная — только когда учить больше нечего: день засчитан, повторять нечего и новых нет
                          // («кнопка серая, хотя я всего 5 слов выучил из 31» — владелец, 2026-10-21).
                          calm={!!stats.data?.dayCounted && plan.repeat === 0 && plan.new === 0}
                          phone={isMobile}
                          onTrain={openTraining}
                        />
                      ) : (
                        <StudyHero phone={isMobile} onStart={() => navigate(paths.training)} />
                      )}
                    </TodayCard>
                  </div>
                )}
                <GameBanner phone={isMobile} />
                {wide && !noModules && <CreateWordCard phone={isMobile} />}
              </div>
              {/* Справа — где получить жетоны: «На сегодня» (задание, миссия, подборка грамматики — владелец
                  2026-10-23) и под ней прогресс грамматики до награды (2026-10-25). Уже 880 px «новое слово» —
                  последним, под грамматикой (без модулей его нет). */}
              <div className={cn('dash-col dash-col-side', gap)}>
                <DailiesCard phone={isMobile} />
                <div className={cn('dash-stack', gap)}>
                  <GrammarCard phone={isMobile} />
                  {!wide && !noModules && <CreateWordCard phone={isMobile} />}
                </div>
              </div>
            </div>
          )}
          {activeQuery.isError && (
            <p role="status" className="text-sm text-muted">
              {t('training.resumeError')}
            </p>
          )}
        </div>
      </div>
      {tourReady && <DashboardTour onDone={() => setTourOn(false)} />}
    </div>
  );
}
